import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Gift, CheckCircle, Clock, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const KIND_LABEL = { gear: "Company gear", giftcard: "Digital gift card", perk: "Perk" };
const RULE_LABEL = {
  wrap_ontime: "On-time shift wrap-up",
  task_tap: "Each care-plan task you confirm",
  training_complete: "Completing a training",
  perfect_week: "Perfect week bonus (+ a wheel spin)",
};

export default function SwagShopPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/points/me").then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ enabled: false, catalog: [], redemptions: [], rules: {}, recent: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-24"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  const balance = data.balance || 0;

  const redeem = async (idx, label) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/points/redeem", { catalog_index: idx });
      toast.success(`Redeemed ${label}! The office will get it to you.`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8" data-testid="swag-shop-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight text-[#4A235A]">Swag Shop</h1>
          <p className="mt-1 text-sm text-stone-500">Points add up for doing the everyday things well. Spend them here — points never expire and never turn into cash.</p>
        </div>
        <Link to="/wrap-wheel" className="inline-flex items-center gap-2 rounded-full border border-[#4A235A]/30 px-4 py-2 text-sm font-semibold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white" data-testid="swag-to-wheel-link">
          <Gift size={16} /> Prize wheel
        </Link>
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-[#B7791F]/30 bg-[#B7791F]/5 px-5 py-4">
        <Star size={36} weight="fill" className="text-[#B7791F]" />
        <div>
          <p className="font-heading text-4xl font-semibold leading-none text-[#4A235A]" data-testid="points-balance">{balance}</p>
          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">your points</p>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">How you earn</p>
        <ul className="mt-2 space-y-1 text-sm text-stone-600" data-testid="points-rules">
          {Object.entries(data.rules || {}).filter(([k]) => k !== "task_tap_cap").map(([k, v]) => (
            <li key={k} className="flex justify-between">
              <span>{RULE_LABEL[k] || k}</span>
              <span className="font-semibold text-[#4A235A]">+{v}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Spend your points</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="points-catalog">
          {(data.catalog || []).map((c, i) => {
            const afford = balance >= c.points;
            const mine = (data.redemptions || []).find((r) => r.label === c.label && r.status === "pending");
            return (
              <div key={i} className="flex items-center justify-between rounded-lg border border-stone-200 px-3 py-2.5" data-testid={`shop-item-${i}`}>
                <div>
                  <p className="text-sm font-medium text-stone-800">{c.label}</p>
                  <p className="text-[11px] text-stone-400">{KIND_LABEL[c.kind] || c.kind}</p>
                </div>
                {mine ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[#B7791F]"><Clock size={12} /> on its way</span>
                ) : (
                  <button onClick={() => redeem(i, c.label)} disabled={!afford || busy}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${afford ? "bg-[#4A235A] text-white hover:bg-stone-900" : "bg-stone-100 text-stone-400"}`}
                    data-testid={`redeem-${i}`}>
                    {c.points} pts
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-stone-400">Company gear: pick up during office pickup windows, or we bring it to you. Gift cards: released by the office after a quick approval — your code shows up right here.</p>
      </div>

      {(data.redemptions || []).filter((r) => r.status === "fulfilled").length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Yours — fulfilled</p>
          <ul className="mt-2 space-y-1.5" data-testid="my-redemptions">
            {data.redemptions.filter((r) => r.status === "fulfilled").map((r, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-stone-700">
                <CheckCircle size={14} className="text-emerald-600" />
                {r.label}
                {r.code && <span className="rounded bg-stone-100 px-2 py-0.5 font-mono text-xs font-semibold text-stone-800" data-testid={`redeemed-code-${i}`}>{r.code}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.recent || []).length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Recent points</p>
          <ul className="mt-2 space-y-1" data-testid="points-recent">
            {data.recent.map((l, i) => (
              <li key={i} className="flex justify-between text-xs text-stone-500">
                <span>{l.reason}</span>
                <span className={l.points >= 0 ? "font-semibold text-emerald-600" : "font-semibold text-stone-500"}>{l.points >= 0 ? `+${l.points}` : l.points}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
